import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../../../components/ui/Header';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { MapPin, Phone, User, DollarSign, X, Plus, Edit2, Trash2, ArrowLeft, Search } from 'lucide-react';
import { API_ENDPOINTS } from '../../../utils/config';

const PropertyMaps = () => {
  const navigate = useNavigate();
  const [mapProperties, setMapProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [formData, setFormData] = useState({
    lat: '',
    long: '',
    address: '',
    agent_name: '',
    agent_number: '',
    area_price: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [mapZoom, setMapZoom] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('properties');
  const [surveys, setSurveys] = useState([]);
  const [isLoadingSurveys, setIsLoadingSurveys] = useState(false);
  const [surveySearchQuery, setSurveySearchQuery] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [surveyCoordinates, setSurveyCoordinates] = useState({}); // Store lat/lng for each survey

  useEffect(() => {
    fetchMapProperties();
    if (activeTab === 'surveys') {
      fetchSurveys();
    }
  }, [activeTab]);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchMapProperties = async () => {
    setIsLoading(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.PROPERTY_MAPS_LIST, { headers });
      
      const propertiesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || []);
      
      // Map the API response to component format
      const mappedProperties = propertiesData.map(prop => ({
        id: prop.id,
        lat: parseFloat(prop.latitude || prop.lat) || 0,
        long: parseFloat(prop.longitude || prop.long) || 0,
        address: prop.address || prop.property_address || '',
        agent_name: prop.agent_name || '',
        agent_number: prop.agent_number || '',
        area_price: prop.area_price || ''
      }));
      
      setMapProperties(mappedProperties);
      
      // Set map center to first property or default
      if (mappedProperties.length > 0) {
        setMapCenter({ lat: mappedProperties[0].lat, lng: mappedProperties[0].long });
      }
    } catch (error) {
      setMapProperties([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.lat || isNaN(parseFloat(formData.lat))) {
      newErrors.lat = 'Valid latitude is required';
    } else {
      const lat = parseFloat(formData.lat);
      if (lat < -90 || lat > 90) {
        newErrors.lat = 'Latitude must be between -90 and 90';
      }
    }

    if (!formData.long || isNaN(parseFloat(formData.long))) {
      newErrors.long = 'Valid longitude is required';
    } else {
      const lng = parseFloat(formData.long);
      if (lng < -180 || lng > 180) {
        newErrors.long = 'Longitude must be between -180 and 180';
      }
    }

    if (!formData.address?.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.agent_name?.trim()) {
      newErrors.agent_name = 'Agent name is required';
    }

    if (!formData.agent_number?.trim()) {
      newErrors.agent_number = 'Agent number is required';
    }

    if (!formData.area_price?.trim()) {
      newErrors.area_price = 'Area price is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setNotification(null);

    try {
      const headers = getAuthHeaders();
      const payload = {
        latitude: formData.lat,
        longitude: formData.long,
        address: formData.address,
        agent_name: formData.agent_name,
        agent_number: formData.agent_number,
        area_price: formData.area_price
      };

      let response;
      if (modalMode === 'edit' && selectedProperty) {
        response = await axios.put(
          API_ENDPOINTS.PROPERTY_MAPS_UPDATE(selectedProperty.id),
          payload,
          { headers }
        );
      } else {
        response = await axios.post(
          API_ENDPOINTS.PROPERTY_MAPS_CREATE,
          payload,
          { headers }
        );
      }

      setNotification({
        type: 'success',
        message: modalMode === 'edit' ? 'Property updated successfully' : 'Property added to map successfully'
      });

      await fetchMapProperties();
      handleCloseModal();
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                       error?.response?.data?.detail || 
                       error?.message || 
                       'Failed to save property. Please try again.';
      setNotification({
        type: 'error',
        message: apiError
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNew = () => {
    setModalMode('add');
    setSelectedProperty(null);
    setFormData({
      lat: '',
      long: '',
      address: '',
      agent_name: '',
      agent_number: '',
      area_price: ''
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleEdit = (property) => {
    setModalMode('edit');
    setSelectedProperty(property);
    setFormData({
      lat: property.lat.toString(),
      long: property.long.toString(),
      address: property.address || '',
      agent_name: property.agent_name || '',
      agent_number: property.agent_number || '',
      area_price: property.area_price || ''
    });
    setErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = async (property) => {
    if (!window.confirm(`Are you sure you want to delete this property from the map?`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      await axios.delete(API_ENDPOINTS.PROPERTY_MAPS_DELETE(property.id), { headers });
      
      setNotification({
        type: 'success',
        message: 'Property removed from map successfully'
      });
      
      await fetchMapProperties();
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                       error?.response?.data?.detail || 
                       'Failed to delete property. Please try again.';
      setNotification({
        type: 'error',
        message: apiError
      });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
    setFormData({
      lat: '',
      long: '',
      address: '',
      agent_name: '',
      agent_number: '',
      area_price: ''
    });
    setErrors({});
  };

  const handleMarkerClick = (property) => {
    setSelectedProperty(property);
    setSelectedSurvey(null); // Clear selected survey when property is clicked
    setMapCenter({ lat: property.lat, lng: property.long });
    setMapZoom(15);
  };

  const handleSurveyClick = async (survey) => {
    setSelectedSurvey(survey);
    const surveyStatus = survey?.status?.toLowerCase() || '';
    
    // If survey status is pending, show message and return
    if (surveyStatus === 'pending') {
      setNotification({
        type: 'info',
        message: 'Yet survey to be done'
      });
      return;
    }

    // For completed surveys, fetch full survey details to get Latitude and Longitude
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.SURVEY_DETAIL(survey.id), { headers });
      const surveyDetails = response.data;

      // Helper function to safely parse coordinate value
      const parseCoordinate = (value) => {
        if (value === null || value === undefined || value === '') return null;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? null : parsed;
      };

      // Extract Latitude and Longitude from survey details
      let lat = null;
      let lng = null;
      let address = surveyDetails?.property_address || surveyDetails?.property_details?.property_address || survey?.property_address || '';

      // Check for Latitude and Longitude fields (capitalized) - these are the primary fields
      if (surveyDetails?.Latitude !== undefined && surveyDetails?.Latitude !== null && 
          surveyDetails?.Longitude !== undefined && surveyDetails?.Longitude !== null) {
        lat = parseCoordinate(surveyDetails.Latitude);
        lng = parseCoordinate(surveyDetails.Longitude);
      } else if (surveyDetails?.latitude && surveyDetails?.longitude) {
        lat = parseCoordinate(surveyDetails.latitude);
        lng = parseCoordinate(surveyDetails.longitude);
      } else if (surveyDetails?.property_details?.Latitude !== undefined && surveyDetails?.property_details?.Latitude !== null && 
                 surveyDetails?.property_details?.Longitude !== undefined && surveyDetails?.property_details?.Longitude !== null) {
        lat = parseCoordinate(surveyDetails.property_details.Latitude);
        lng = parseCoordinate(surveyDetails.property_details.Longitude);
      } else if (surveyDetails?.property_details?.latitude && surveyDetails?.property_details?.longitude) {
        lat = parseCoordinate(surveyDetails.property_details.latitude);
        lng = parseCoordinate(surveyDetails.property_details.longitude);
      } else if (surveyDetails?.property?.Latitude !== undefined && surveyDetails?.property?.Latitude !== null && 
                 surveyDetails?.property?.Longitude !== undefined && surveyDetails?.property?.Longitude !== null) {
        lat = parseCoordinate(surveyDetails.property.Latitude);
        lng = parseCoordinate(surveyDetails.property.Longitude);
      } else if (surveyDetails?.property?.latitude && surveyDetails?.property?.longitude) {
        lat = parseCoordinate(surveyDetails.property.latitude);
        lng = parseCoordinate(surveyDetails.property.longitude);
      }

      // Store coordinates in state for display
      if (lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng)) {
        setSurveyCoordinates(prev => ({
          ...prev,
          [survey.id]: { lat, lng }
        }));
        
        // Update map center
        setMapCenter({ lat, lng });
        setMapZoom(15);
        
        // Create a temporary property object for display
        setSelectedProperty({
          id: survey.id,
          lat,
          long: lng,
          address: address || 'Survey Location',
          survey_number: surveyDetails?.survey_number || survey.survey_number,
          survey: surveyDetails
        });
      } else {
        // If no coordinates found for completed survey, show error
        setNotification({
          type: 'error',
          message: `No coordinates (Latitude/Longitude) found for completed survey. Survey Number: ${surveyDetails?.survey_number || survey.survey_number || 'N/A'}`
        });
      }
    } catch (error) {
      console.error('Error fetching survey details:', error);
      setNotification({
        type: 'error',
        message: `Failed to fetch survey details. Please try again.`
      });
    }
  };

  // Fetch surveys
  const fetchSurveys = async () => {
    setIsLoadingSurveys(true);
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(API_ENDPOINTS.SURVEYS_LIST, { headers });
      
      const surveysData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.results || response.data?.data || response.data?.surveys || []);
      
      setSurveys(surveysData);
    } catch (error) {
      setSurveys([]);
    } finally {
      setIsLoadingSurveys(false);
    }
  };

  // Filter properties based on search query
  const filteredProperties = mapProperties.filter(property => {
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (property.address?.toLowerCase().includes(query)) ||
      (property.agent_name?.toLowerCase().includes(query)) ||
      (property.agent_number?.toLowerCase().includes(query)) ||
      (property.area_price?.toLowerCase().includes(query))
    );
  });

  // Filter surveys based on search query
  const filteredSurveys = surveys.filter(survey => {
    if (!surveySearchQuery.trim()) return true;
    
    const query = surveySearchQuery.toLowerCase();
    const surveyorName = survey.surveyor?.username || survey.surveyor?.email || '';
    const customerName = survey.customer_name || survey.client_name || '';
    
    return (
      (survey.survey_number?.toLowerCase().includes(query)) ||
      (survey.property_address?.toLowerCase().includes(query)) ||
      (survey.status?.toLowerCase().includes(query)) ||
      (surveyorName.toLowerCase().includes(query)) ||
      (customerName.toLowerCase().includes(query))
    );
  });

  const getMapUrl = () => {
    // Always use current mapCenter and mapZoom
    return `https://www.google.com/maps?q=${mapCenter.lat},${mapCenter.lng}&z=${mapZoom}&output=embed&hl=en&gl=US`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="pt-16 flex items-center justify-center h-screen">
          <div className="flex items-center space-x-2">
            <Icon name="Loader2" size={24} className="animate-spin" color="var(--color-primary)" />
            <span className="text-foreground">Loading map properties...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="p-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/property-management')}
                  className="p-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <h1 className="text-2xl font-bold text-foreground">Property Maps</h1>
              </div>
              <p className="text-muted-foreground ml-11">
                View and manage properties on an interactive map with agent and pricing information
              </p>
            </div>
            <Button
              variant="default"
              iconName="Plus"
              iconPosition="left"
              onClick={handleAddNew}
              className="mt-4 sm:mt-0"
            >
              Add Property to Map
            </Button>
          </div>

          {/* Notification */}
          {notification && (
            <div className={`mb-6 p-4 rounded-lg border ${
              notification.type === 'success' 
                ? 'border-green-300 bg-green-50 text-green-700' 
                : notification.type === 'info'
                ? 'border-blue-300 bg-blue-50 text-blue-700'
                : 'border-red-300 bg-red-50 text-red-700'
            }`}>
              <p className="text-sm font-medium">{notification.message}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map Section */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-primary" />
                      <h2 className="text-lg font-semibold text-foreground">Property Locations</h2>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {mapProperties.length} {mapProperties.length === 1 ? 'property' : 'properties'}
                    </span>
                  </div>
                </div>
                
                {/* Map Container */}
                <div className="relative h-[600px] bg-muted">
                  <iframe
                    key={`${mapCenter.lat}-${mapCenter.lng}-${mapZoom}`}
                    width="100%"
                    height="100%"
                    loading="lazy"
                    title="Property Map"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={getMapUrl()}
                    className="border-0"
                  />
                </div>
              </div>
            </div>

            {/* Tabbed List Section */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-lg border border-border">
                {/* Tabs */}
                <div className="flex border-b border-border">
                  <button
                    onClick={() => {
                      setActiveTab('properties');
                      setSelectedSurvey(null); // Clear selected survey when switching tabs
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'properties'
                        ? 'text-primary border-b-2 border-primary bg-primary/5'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    Properties List
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('surveys');
                      setSelectedProperty(null); // Clear selected property when switching tabs
                    }}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                      activeTab === 'surveys'
                        ? 'text-primary border-b-2 border-primary bg-primary/5'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    Survey List
                  </button>
                </div>

                {/* Tab Content */}
                <div className="p-4">
                  {/* Properties Tab */}
                  {activeTab === 'properties' && (
                    <>
                      {/* Search Input */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search by address, agent, phone, or price..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      {searchQuery && (
                        <p className="text-xs text-muted-foreground mb-4">
                          {filteredProperties.length} {filteredProperties.length === 1 ? 'property' : 'properties'} found
                        </p>
                      )}
                      <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {mapProperties.length === 0 ? (
                          <div className="text-center py-8">
                            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No properties on map</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleAddNew}
                              className="mt-4"
                            >
                              Add First Property
                            </Button>
                          </div>
                        ) : filteredProperties.length === 0 ? (
                          <div className="text-center py-8">
                            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No properties found matching "{searchQuery}"</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSearchQuery('')}
                              className="mt-4"
                            >
                              Clear Search
                            </Button>
                          </div>
                        ) : (
                          filteredProperties.map((property, index) => (
                            <div
                              key={property.id || index}
                              className={`p-4 rounded-lg border border-border cursor-pointer transition-colors ${
                                selectedProperty?.id === property.id
                                  ? 'bg-primary/10 border-primary'
                                  : 'bg-card hover:bg-muted/50'
                              }`}
                              onClick={() => handleMarkerClick(property)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h3 className="font-medium text-foreground text-sm mb-1">
                                    {property.address || `Property ${index + 1}`}
                                  </h3>
                                  <div className="flex items-center space-x-1 text-xs text-muted-foreground mb-2">
                                    <MapPin className="w-3 h-3" />
                                    <span>{property.lat.toFixed(4)}, {property.long.toFixed(4)}</span>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleEdit(property);
                                    }}
                                    className="p-1 hover:bg-muted rounded"
                                    title="Edit"
                                  >
                                    <Edit2 className="w-4 h-4 text-muted-foreground" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(property);
                                    }}
                                    className="p-1 hover:bg-muted rounded"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="space-y-1 text-xs">
                                <div className="flex items-center space-x-2">
                                  <User className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-foreground">{property.agent_name || 'N/A'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Phone className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-foreground">{property.agent_number || 'N/A'}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <DollarSign className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-foreground font-medium">{property.area_price || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}

                  {/* Surveys Tab */}
                  {activeTab === 'surveys' && (
                    <>
                      {/* Search Input */}
                      <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          type="text"
                          placeholder="Search by survey number, address, status, or surveyor..."
                          value={surveySearchQuery}
                          onChange={(e) => setSurveySearchQuery(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                      {surveySearchQuery && (
                        <p className="text-xs text-muted-foreground mb-4">
                          {filteredSurveys.length} {filteredSurveys.length === 1 ? 'survey' : 'surveys'} found
                        </p>
                      )}
                      <div className="space-y-3 max-h-[600px] overflow-y-auto">
                        {isLoadingSurveys ? (
                          <div className="text-center py-8">
                            <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-3" color="var(--color-primary)" />
                            <p className="text-muted-foreground">Loading surveys...</p>
                          </div>
                        ) : surveys.length === 0 ? (
                          <div className="text-center py-8">
                            <Icon name="ClipboardList" size={48} className="text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No surveys found</p>
                          </div>
                        ) : filteredSurveys.length === 0 ? (
                          <div className="text-center py-8">
                            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No surveys found matching "{surveySearchQuery}"</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSurveySearchQuery('')}
                              className="mt-4"
                            >
                              Clear Search
                            </Button>
                          </div>
                        ) : (
                          filteredSurveys.map((survey, index) => (
                            <div
                              key={survey.id || index}
                              className={`p-4 rounded-lg border border-border transition-colors cursor-pointer ${
                                selectedSurvey?.id === survey.id
                                  ? 'bg-primary/10 border-primary'
                                  : 'bg-card hover:bg-muted/50'
                              }`}
                              onClick={() => handleSurveyClick(survey)}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex-1">
                                  <h3 className="font-medium text-foreground text-sm mb-1">
                                    {survey.survey_number || `Survey ${index + 1}`}
                                  </h3>
                                  <div className="space-y-1 mb-2">
                                    <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                      <MapPin className="w-3 h-3" />
                                      <span>{survey.property_address || 'N/A'}</span>
                                    </div>
                                    {surveyCoordinates[survey.id] && (
                                      <div className="flex items-center space-x-1 text-xs text-muted-foreground ml-4">
                                        <span className="text-muted-foreground/70">
                                          Lat: {surveyCoordinates[survey.id].lat.toFixed(6)}, Lng: {surveyCoordinates[survey.id].lng.toFixed(6)}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-1">
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    survey.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    survey.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                                    survey.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {survey.status?.replace('_', ' ') || 'N/A'}
                                  </span>
                                </div>
                              </div>
                              
                              <div className="space-y-1 text-xs">
                                {(survey.customer_name || survey.client_name) && (
                                  <div className="flex items-center space-x-2">
                                    <User className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-foreground">
                                      Client: {survey.customer_name || survey.client_name || 'N/A'}
                                    </span>
                                  </div>
                                )}
                                {survey.surveyor && (
                                  <div className="flex items-center space-x-2">
                                    <User className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-foreground">
                                      Surveyor: {survey.surveyor?.username || survey.surveyor?.email || 'N/A'}
                                    </span>
                                  </div>
                                )}
                                {survey.created_at && (
                                  <div className="flex items-center space-x-2">
                                    <span className="text-muted-foreground">
                                      Created: {new Date(survey.created_at).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Selected Property Details */}
          {selectedProperty && (
            <div className="mt-6 bg-card rounded-lg border border-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Property Details</h3>
                  <p className="text-muted-foreground text-sm">{selectedProperty.address}</p>
                </div>
                <button
                  onClick={() => setSelectedProperty(null)}
                  className="p-1 hover:bg-muted rounded"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Coordinates</label>
                    <p className="text-sm text-foreground font-mono">
                      {selectedProperty.lat.toFixed(6)}, {selectedProperty.long.toFixed(6)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Address</label>
                    <p className="text-sm text-foreground">{selectedProperty.address || 'N/A'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Agent Name</label>
                    <p className="text-sm text-foreground">{selectedProperty.agent_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Agent Number</label>
                    <p className="text-sm text-foreground">{selectedProperty.agent_number || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Area Price</label>
                    <p className="text-sm text-foreground font-semibold">{selectedProperty.area_price || 'N/A'}</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(selectedProperty)}
                >
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Property
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    window.open(`https://www.google.com/maps?q=${selectedProperty.lat},${selectedProperty.long}`, '_blank');
                  }}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  View on Google Maps
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg border border-border w-full max-w-2xl shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">
                  {modalMode === 'edit' ? 'Edit Property' : 'Add Property to Map'}
                </h2>
                <button
                  onClick={handleCloseModal}
                  className="p-1 hover:bg-muted rounded"
                >
                  <X className="w-5 h-5 text-muted-foreground" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Latitude <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.lat}
                      onChange={(e) => handleInputChange('lat', e.target.value)}
                      placeholder="e.g., 40.7128"
                      error={errors.lat}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Longitude <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="any"
                      value={formData.long}
                      onChange={(e) => handleInputChange('long', e.target.value)}
                      placeholder="e.g., -74.0060"
                      error={errors.long}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Address <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Enter property address"
                    error={errors.address}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Agent Name <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="text"
                      value={formData.agent_name}
                      onChange={(e) => handleInputChange('agent_name', e.target.value)}
                      placeholder="Enter agent name"
                      error={errors.agent_name}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Agent Number <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="tel"
                      value={formData.agent_number}
                      onChange={(e) => handleInputChange('agent_number', e.target.value)}
                      placeholder="Enter agent phone number"
                      error={errors.agent_number}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Area Price <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.area_price}
                    onChange={(e) => handleInputChange('area_price', e.target.value)}
                    placeholder="Enter area price (e.g., ₹50,000/sqft)"
                    error={errors.area_price}
                  />
                </div>

                <div className="flex items-center justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={isSubmitting}
                    loading={isSubmitting}
                  >
                    {modalMode === 'edit' ? 'Update Property' : 'Add Property'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyMaps;

